import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faDownload,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons'

import getDc3AttestationAssigned from 'api/administration/courses/GetDc3AttestationAssigned'
import getNormativeParticipantsDataValidation from 'api/administration/courses/GetNormativeParticipantsDataValidation'
import downloadNormativeIncompleteParticipantsReport from 'api/administration/courses/DownloadNormativeIncompleteParticipantsReport'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { formatThousandsSeparator } from 'helpers/others.helper'
import { saveAs } from 'helpers'
import { useNotification } from 'hooks/notifications/useNotification'
import { useAccess } from 'contexts/AccessContext'
import { IMainState } from 'store/states'

import { prefix } from '../../constants'
import {
  Banner,
  BannerBody,
  BannerContent,
  BannerIcon,
  BannerTitle,
  DownloadButton,
} from './styled'

interface NormativeIncompleteParticipantsBannerProps {
  courseId: string
}

const NormativeIncompleteParticipantsBanner: React.FC<NormativeIncompleteParticipantsBannerProps> =
  ({ courseId }) => {
    const { i18n } = useTranslation()
    const notification = useNotification()
    const { access } = useAccess()
    const canDownloadReport = access.canDownloadReportWithViewAccess()
    const attestationsEnabled = useSelector(
      ({ general }: IMainState) => general.permissions.attestationsEnabled
    )
    const t = useTranslationRankmi({
      prefix: `${prefix}.normativeIncompleteParticipantsBanner`,
    })
    const tValidation = useTranslationRankmi({
      prefix: 'administration.courses.list.normativeDataValidation',
    })

    const [incompleteCount, setIncompleteCount] = useState(0)
    const [loading, setLoading] = useState(true)
    const [downloading, setDownloading] = useState(false)

    useEffect(() => {
      const numericCourseId = Number(courseId)

      if (!attestationsEnabled || !numericCourseId) {
        setIncompleteCount(0)
        setLoading(false)
        return
      }

      let cancelled = false

      const loadValidation = async () => {
        try {
          const { normative } = await getDc3AttestationAssigned(numericCourseId)

          if (!normative || cancelled) {
            return
          }

          const { incompleteParticipantsCount } =
            await getNormativeParticipantsDataValidation(numericCourseId)

          if (!cancelled) {
            setIncompleteCount(incompleteParticipantsCount)
          }
        } catch {
          if (!cancelled) {
            setIncompleteCount(0)
          }
        } finally {
          if (!cancelled) {
            setLoading(false)
          }
        }
      }

      loadValidation()

      return () => {
        cancelled = true
      }
    }, [attestationsEnabled, courseId])

    const handleDownloadReport = async () => {
      const numericCourseId = Number(courseId)

      if (!numericCourseId || downloading) return

      setDownloading(true)
      try {
        const { blob, filename } =
          await downloadNormativeIncompleteParticipantsReport(
            numericCourseId,
            'monitoring'
          )
        saveAs(blob, filename)
      } catch {
        notification.error(tValidation('notifications.error'))
      } finally {
        setDownloading(false)
      }
    }

    if (loading || incompleteCount <= 0 || !canDownloadReport) {
      return null
    }

    const formattedCount = formatThousandsSeparator(
      incompleteCount,
      i18n.language
    )

    return (
      <Banner>
        <BannerIcon>
          <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden />
        </BannerIcon>
        <BannerContent>
          <BannerTitle>
            {(incompleteCount === 1
              ? t('title', { count: incompleteCount })
              : t('titlePlural', { count: incompleteCount })
            ).replace(String(incompleteCount), formattedCount)}
          </BannerTitle>
          <BannerBody>{t('body')}</BannerBody>
        </BannerContent>
        <DownloadButton
          type="button"
          onClick={handleDownloadReport}
          disabled={downloading}
        >
          <FontAwesomeIcon icon={faDownload} aria-hidden />
          {t('downloadReport')}
        </DownloadButton>
      </Banner>
    )
  }

export default NormativeIncompleteParticipantsBanner
